/**
 * Collaboration client + Changes / Review / Issues IA helpers.
 * Presence is auxiliary (who is on a change/review/issue), not a canvas.
 */

import { apiGet, apiJson, type Role } from "./api.ts";

export type ReviewVerdict = "approved" | "changes_requested";

export type JobCheckLike = {
  id: string;
  kind: string;
  kind_detail?: string | null;
  state: string;
  error?: string | null;
  check_status?: string;
};

export type CheckConclusion =
  | "none"
  | "queued"
  | "running"
  | "passed"
  | "failed"
  | "cancelled"
  | "timed_out"
  | "inconclusive";

export type ChangeListItem = {
  schema: "pcbx.change/1";
  id: string;
  project_id: string;
  baseline_snapshot: string;
  candidate_snapshot: string;
  baseline_oid: string | null;
  candidate_oid: string | null;
  title: string;
  workstream: string;
  review_id: string;
  review_status: string;
  check_conclusion: CheckConclusion;
  check_run_id: string | null;
  check_kind: string | null;
  created_by?: string | null;
  created_at: string;
};

export type PresenceViewer = {
  subject: string;
  surface: "changes" | "review" | "issues";
  object_id: string | null;
  display_name: string | null;
  seen_at: string;
  expires_at: string;
};

/** V1.1 aux marker — dashed, never copper-underline. */
export const PRESENCE_AUX_LABEL = "Presence V1.1" as const;

export type PresenceSnapshot = {
  schema: "pcbx.presence/1";
  project_id: string;
  at: string;
  viewers: PresenceViewer[];
};

export type CollabSurface = "changes" | "review" | "issues";

function projectApi(org: string, project: string, tail: string): string {
  return `/api/o/${encodeURIComponent(org)}/p/${encodeURIComponent(project)}/${tail}`;
}

export async function listChanges(org: string, project: string): Promise<{ items: ChangeListItem[] }> {
  const rows = await apiGet<ChangeListItem[]>(projectApi(org, project, "collaboration/changes"));
  return { items: Array.isArray(rows) ? rows : [] };
}

export async function getChange(org: string, project: string, changeId: string): Promise<ChangeListItem> {
  return apiGet<ChangeListItem>(projectApi(org, project, `changes/${encodeURIComponent(changeId)}`));
}

export async function decideReview(
  org: string,
  project: string,
  reviewId: string,
  verdict: ReviewVerdict,
): Promise<{ status?: string; id?: string }> {
  return apiJson<{ status?: string; id?: string }>(
    projectApi(org, project, `reviews/${encodeURIComponent(reviewId)}/decide`),
    "POST",
    { verdict, status: verdict },
  );
}

export async function createChange(
  org: string,
  project: string,
  body: {
    baseline_snapshot: string;
    candidate_snapshot: string;
    baseline_oid?: string | null;
    candidate_oid?: string | null;
    title?: string;
    workstream?: string;
    idempotency_key?: string;
  },
) {
  return apiJson<ChangeListItem>(projectApi(org, project, "collaboration/changes"), "POST", {
    ...body,
    request_review: true,
  });
}

export async function listPresence(org: string, project: string): Promise<PresenceSnapshot> {
  return apiGet<PresenceSnapshot>(projectApi(org, project, "collaboration/presence"));
}

export async function publishPresence(
  org: string,
  project: string,
  body: { surface: CollabSurface; object_id?: string | null; display_name?: string | null },
): Promise<PresenceSnapshot> {
  const res = await apiJson<{ viewers?: PresenceViewer[] } | PresenceSnapshot>(
    projectApi(org, project, "collaboration/presence"),
    "PUT",
    body,
  );
  if (res && "viewers" in res && Array.isArray(res.viewers)) return res as PresenceSnapshot;
  if (res && "snapshot" in (res as { snapshot?: PresenceSnapshot })) {
    return (res as { snapshot: PresenceSnapshot }).snapshot;
  }
  return { schema: "pcbx.presence/1", project_id: "", at: new Date().toISOString(), viewers: [] };
}

export function canWriteCollaboration(role: Role | null | undefined): boolean {
  return role != null && role !== "guest" && role !== "viewer";
}

/** UnderlineNav Issues badge = open rows from the list, not a second counter table. */
export function openIssueCount(issues: { status?: string | null }[] | null | undefined): number {
  return (issues ?? []).filter((issue) => issue.status === "open").length;
}

export function issueStatusLabel(status: string | null | undefined): string {
  return status === "closed" ? "Closed" : "Open";
}

/** Create/edit refuse a silent tracker without a git ref. */
export function requireIssueGitRef(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export type IssueCommentEventResponse = {
  id: string;
  issue_id: string;
  type: "comment" | "status";
  version: number;
  comment_id?: string;
  comment?: IssueComment;
};

export type IssueComment = {
  id: string;
  project_id: string;
  issue_id: string;
  author_user_id: string;
  body: string;
  created_at: string;
  origin?: "ui" | "think" | null;
  think_tool_call_id?: string | null;
  author_display_name?: string | null;
  author_origin_marker?: "ui" | "think" | null;
};

export async function writeIssueEvent(
  org: string,
  project: string,
  issueId: string,
  body: {
    type: "comment" | "status";
    body?: string;
    status?: "open" | "closed";
    idempotency_key?: string;
  },
): Promise<IssueCommentEventResponse> {
  return apiJson<IssueCommentEventResponse>(
    projectApi(org, project, `issues/${encodeURIComponent(issueId)}/events`),
    "POST",
    body,
  );
}

export function canDecideReview(role: Role | null | undefined): boolean {
  return role === "owner" || role === "review" || role === "editor";
}

/** Same bag as worker `isJobsCheckRun` — circuit_diff / agent_findings only. */
export function isJobsCheckRun(job: Pick<JobCheckLike, "kind" | "kind_detail">): boolean {
  const detail = (job.kind_detail ?? "").trim();
  if (detail === "circuit_diff" || detail === "agent_findings") return true;
  return job.kind === "circuit_diff";
}

export function jobsCheckRuns(runs: JobCheckLike[]): JobCheckLike[] {
  return runs.filter(isJobsCheckRun);
}

function conclusionFromRun(run: JobCheckLike): CheckConclusion {
  const state = run.state;
  if (state === "queued" || state === "pending") return "queued";
  if (state === "running") return "running";
  if (state === "cancelled") return "cancelled";
  if (state === "failed") {
    if (typeof run.error === "string" && /timed.?out|requeue exhausted|stayed queued/i.test(run.error)) {
      return "timed_out";
    }
    return "failed";
  }
  if (state === "succeeded" || state === "passed") return "passed";
  if (state === "timed_out") return "timed_out";
  if (state === "inconclusive") return "inconclusive";
  return "inconclusive";
}

export function checkConclusionFromRuns(runs: JobCheckLike[]): {
  conclusion: CheckConclusion;
  run: JobCheckLike | null;
} {
  const checks = jobsCheckRuns(runs);
  if (checks.length === 0) return { conclusion: "none", run: null };
  const run = checks[0]!;
  return { conclusion: conclusionFromRun(run), run };
}

/** Approve requires a succeeded JobsChecks run. Empty findings are not a pass. */
export function reviewDecideGate(
  runs: JobCheckLike[],
  verdict: ReviewVerdict,
): { ok: true } | { ok: false; message: string } {
  const { conclusion, run } = checkConclusionFromRuns(runs);
  if (verdict === "changes_requested") return { ok: true };
  if (!run || conclusion === "none") {
    return { ok: false, message: "cannot approve without a JobsChecks check run" };
  }
  if (conclusion === "queued" || conclusion === "running") {
    return { ok: false, message: `check run ${run.id} is ${conclusion}` };
  }
  if (conclusion !== "passed") {
    return { ok: false, message: `check run ${run.id} is ${conclusion}; empty or failed findings are not a pass` };
  }
  return { ok: true };
}

/** UI disable for Falcon Approve — role + `reviewDecideGate`. */
export function reviewApproveUi(
  runs: JobCheckLike[],
  role: Role | null | undefined,
): { disabled: boolean; reason: string | null } {
  if (!canDecideReview(role)) {
    return { disabled: true, reason: "review role required" };
  }
  const gate = reviewDecideGate(runs, "approved");
  if (!gate.ok) return { disabled: true, reason: gate.message };
  return { disabled: false, reason: null };
}

export function findingPinLabel(finding: { cite?: Record<string, unknown> | null; summary?: string }): string | null {
  const cite = finding.cite ?? {};
  const refdes = typeof cite.refdes === "string" ? cite.refdes : "";
  const pinName =
    (typeof cite.pin_name_to === "string" && cite.pin_name_to) ||
    (typeof cite.pin_name === "string" && cite.pin_name) ||
    (typeof cite.pin_number === "string" && cite.pin_number) ||
    "";
  if (refdes && pinName) return `${refdes}.${pinName}`;
  return null;
}

export function changeOidRange(input: {
  baseline_oid?: string | null;
  candidate_oid?: string | null;
  baseline_snapshot?: string | null;
  candidate_snapshot?: string | null;
  from_oid?: string | null;
  to_oid?: string | null;
  from_ref?: string | null;
  to_ref?: string | null;
}): { from: string; to: string; label: string } {
  const from = input.baseline_oid || input.from_oid || input.baseline_snapshot || input.from_ref || "";
  const to = input.candidate_oid || input.to_oid || input.candidate_snapshot || input.to_ref || "";
  const short = (value: string) => (value.length > 7 ? value.slice(0, 7) : value);
  return {
    from,
    to,
    label: from && to ? `${short(from)} ↔ ${short(to)}` : short(from || to),
  };
}

export function checkConclusionLabel(conclusion: CheckConclusion | string | null | undefined): string {
  switch (conclusion) {
    case "passed":
      return "Checks passed";
    case "failed":
      return "Checks failed";
    case "queued":
    case "running":
      return `Checks ${conclusion}`;
    case "cancelled":
    case "timed_out":
    case "inconclusive":
      return `Checks ${conclusion} — not a pass`;
    default:
      return "No JobsChecks run";
  }
}

export function reviewStatusLabel(status: string | null | undefined): string {
  if (status === "approved") return "Approved";
  if (status === "changes_requested") return "Changes requested";
  if (status === "open") return "Open";
  if (status === "merged") return "Merged";
  if (status === "closed") return "Closed";
  return status || "No review";
}

/** Missing / unparsable expiry is gone — presence is never treated as durable SoT. */
export function isLivePresenceViewer(viewer: Pick<PresenceViewer, "expires_at">, nowMs = Date.now()): boolean {
  if (!viewer.expires_at) return false;
  const exp = Date.parse(viewer.expires_at);
  return !Number.isNaN(exp) && exp > nowMs;
}

export function viewersOnSurface(
  snapshot: PresenceSnapshot | null,
  surface: CollabSurface,
  objectId?: string | null,
  nowMs = Date.now(),
): PresenceViewer[] {
  if (!snapshot) return [];
  return snapshot.viewers.filter((viewer) => {
    if (!isLivePresenceViewer(viewer, nowMs)) return false;
    if (viewer.surface !== surface) return false;
    if (objectId && viewer.object_id && viewer.object_id !== objectId) return false;
    return true;
  });
}

/** IA copy: Changes are snapshot diffs; Review is a stage; presence is not the product. */
export const COLLAB_IA = {
  changesKicker: "change · baseline ↔ candidate",
  reviewKicker: "review of a change · not a canvas",
  issuesKicker: "track · git_ref required",
  presenceHint: "Presence V1.1 is ephemeral who-is-viewing, not a shared document.",
  noMerge: "Collaboration does not merge or write project source files.",
  checksHint: "Review conclusions come from JobsChecks runs. Empty findings are not a pass.",
} as const;
